/**
 * @implements US-AUTH-001 Sign In Page
 */

import { track } from '@app/analytics/client';
import { authClient } from '@app/auth/client';
import * as Sentry from '@sentry/react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@app/ui';
import { AnimatePresenceWrapper } from '@app/ui/motion';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { RouteErrorComponent } from '../../components/route-error';

export const Route = createFileRoute('/auth/login')({
  validateSearch: (search: Record<string, unknown>) => ({
    returnUrl: (search.returnUrl as string) || '/dashboard',
  }),
  component: SignInPage,
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
});

function SignInPage() {
  const { returnUrl } = Route.useSearch();

  // CRITICAL: callbackURL MUST be absolute for Better Auth + oAuthProxy.
  // Relative URLs resolve against the auth API origin, not the web app origin,
  // causing the user to land on the wrong page after OAuth.
  // Ref: inspectconnect-app apps/web/src/lib/auth-routing.ts
  const absoluteCallbackURL =
    typeof window !== 'undefined' ? `${window.location.origin}${returnUrl}` : returnUrl;

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    // Wrap the entire user-visible OAuth initiation in a Sentry span so
    // browserTracingIntegration's auto-spans are joined under a named root
    // ("auth.oauth.google"). Distinct fingerprints make the OAuth path
    // easy to slice in Sentry's tracing UI, and the span carries the
    // outcome ("redirected" | "swallowed" | "failed") so we can finally
    // see WHY the 06-09 user rage-clicked at /dashboard after sign-in.
    await Sentry.startSpan(
      {
        name: 'auth.oauth.google',
        op: 'auth.oauth',
        attributes: {
          'auth.provider': 'google',
          'auth.return_url': returnUrl,
        },
      },
      async (span) => {
        // Run analytics + storage side-effects in a try block — sessionStorage
        // can throw in private-mode / cookies-blocked browsers, and
        // `posthog.capture` was called direct (not via the guarded `track`)
        // so an uninitialized posthog would throw, aborting the click handler
        // BEFORE the auth POST fires. Symptom: click → nothing happens, no
        // error in console, no network request. (US-031.4 debugging session.)
        try {
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('signin_method', 'google');
          }
          track('signin_started', { method: 'google', return_url: returnUrl });
          try {
            posthog.capture('auth_method_selected', { method: 'google' });
          } catch (e) {
            // posthog can be undefined or partially initialized in some
            // preview / extension-blocked contexts; swallow + log so the
            // sign-in itself proceeds.
            console.warn('[auth] posthog.capture failed (non-fatal):', e);
          }
        } catch (e) {
          console.warn('[auth] pre-auth side-effects failed (non-fatal):', e);
        }
        try {
          await authClient.signIn.social({
            provider: 'google',
            callbackURL: absoluteCallbackURL,
          });
          // Better Auth's client auto-redirects on `{redirect:true,url}`.
          // Set the optimistic outcome SYNCHRONOUSLY here so it's serialized
          // onto the span before the page navigates away — otherwise the
          // span ends with no outcome (Bugbot #5f23739c on PR#157). If the
          // redirect ACTUALLY happens, the page navigates and this value
          // is the one that ships. If it gets swallowed, the post-await
          // 1.5s check below overrides it.
          span.setAttribute('auth.outcome', 'redirected');
          // Race the redirect against a 1.5s timeout. If we win the race
          // (still on /auth/login after 1.5s), the redirect was swallowed
          // — override the outcome attribute and surface UI feedback. The
          // span ends only after this await resolves, so the override
          // attribute lands BEFORE the span is serialized to Sentry.
          await new Promise<void>((resolve) => {
            setTimeout(() => {
              if (
                typeof window !== 'undefined' &&
                window.location.pathname.startsWith('/auth/login')
              ) {
                span.setAttribute('auth.outcome', 'redirect_swallowed');
                Sentry.captureMessage('auth.oauth.google redirect swallowed', 'warning');
                setError(
                  'Browser blocked the redirect to Google. Try magic link, or check for an extension blocking pop-ups.',
                );
                setLoading(false);
              }
              resolve();
            }, 1500);
          });
        } catch (e) {
          // Log the real error to console so devtools shows what actually
          // failed — silent `catch {}` was hiding the diagnostic info.
          console.error('[auth] Google sign-in failed:', e);
          span.setAttribute('auth.outcome', 'error');
          Sentry.captureException(e, { tags: { auth_provider: 'google' } });
          setError(
            e instanceof Error && e.message
              ? `Failed to sign in with Google: ${e.message}`
              : 'Failed to sign in with Google. Please try again.',
          );
          setLoading(false);
        }
      },
    );
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError(null);
    await Sentry.startSpan(
      {
        name: 'auth.oauth.magic_link',
        op: 'auth.oauth',
        attributes: {
          'auth.provider': 'magic_link',
          'auth.return_url': returnUrl,
        },
      },
      async (span) => {
        // Same defensive wrap as the Google handler — sessionStorage + analytics
        // were running outside the try block, so an exception in either would
        // kill the click before the network call. See handleGoogleSignIn for
        // detail.
        try {
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('signin_method', 'magic_link');
          }
          track('signin_started', { method: 'magic_link', return_url: returnUrl });
        } catch (e) {
          console.warn('[auth] pre-auth side-effects failed (non-fatal):', e);
        }
        try {
          const result = await authClient.signIn.magicLink({
            email,
            callbackURL: absoluteCallbackURL,
          });
          if (result.error) {
            console.error('[auth] magic-link error:', result.error);
            span.setAttribute('auth.outcome', 'rejected');
            span.setAttribute('auth.error_code', result.error.code ?? 'unknown');
            Sentry.captureMessage(`auth.oauth.magic_link rejected: ${result.error.code}`, 'warning');
            setError(result.error.message || 'Failed to send magic link. Please try again.');
          } else {
            // Don't send raw email — with `person_profiles: 'identified_only'`
            // any property here lands on the anonymous distinctId where it's
            // harder to redact than person traits. We already capture the
            // email on identify() after the magic link is clicked.
            try {
              posthog.capture('magic_link_requested');
            } catch (e) {
              console.warn('[auth] posthog.capture failed (non-fatal):', e);
            }
            span.setAttribute('auth.outcome', 'link_sent');
            setMagicLinkSent(true);
          }
        } catch (e) {
          console.error('[auth] magic-link failed:', e);
          span.setAttribute('auth.outcome', 'error');
          Sentry.captureException(e, { tags: { auth_provider: 'magic_link' } });
          setError(
            e instanceof Error && e.message
              ? `Failed to send magic link: ${e.message}`
              : 'Failed to send magic link. Please try again.',
          );
        } finally {
          setLoading(false);
        }
      },
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <AnimatePresence mode="wait">
        {magicLinkSent ? (
          <motion.div
            key="check-email"
            initial={hasMounted ? { opacity: 0, y: 8 } : false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-md"
          >
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Check your email</CardTitle>
                <CardDescription>
                  We sent a magic link to <strong>{email}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  Click the link in your email to sign in. The link will expire in 5 minutes.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setMagicLinkSent(false)}
                >
                  Use a different email
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="sign-in"
            initial={hasMounted ? { opacity: 0, y: 8 } : false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-md"
          >
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Sign in to your account</CardTitle>
                <CardDescription>Choose your preferred sign-in method</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <AnimatePresenceWrapper show={!!error}>
                  <div className="p-3 rounded-md bg-destructive/10 border border-destructive/50 text-destructive text-sm">
                    {error}
                  </div>
                </AnimatePresenceWrapper>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                <form onSubmit={handleMagicLink} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email-magic">Email</Label>
                    <Input
                      id="email-magic"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      autoComplete="email"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Sending...' : 'Send magic link'}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    We'll email you a link to sign in instantly
                  </p>
                </form>

                <div className="text-center text-sm text-muted-foreground">
                  <p>
                    Don't have an account?{' '}
                    <Link
                      to="/auth/login"
                      search={{ returnUrl }}
                      className="text-primary hover:underline"
                    >
                      Sign up
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
